/**
 * CPV 2008 — the Common Procurement Vocabulary (Regulation (EC) No 213/2008),
 * the classification axis of the Spanish edition, the way ИКПУ is for UZ and
 * ЕНС ТРУ for KZ. Pure helpers, shared by the API (ingest, procurement kind,
 * categories, the reference seed) and the web.
 *
 * ## The code
 *
 * Eight digits, plus a check digit after a dash in the EU's own tables
 * (`45233140-2`). PLACSP publishes the bare eight (`45233140`), and that bare
 * form is what `tenders.ikpu` and `ikpu_codes.code` store for Spain —
 * {@link canonicalCpv} turns either spelling into it.
 *
 * ## The hierarchy
 *
 * Read from the trailing zeros. The significant prefix — the code without its
 * trailing zeros, never shorter than two digits — names the node:
 *
 *   45000000  division  (2 significant digits)  «Trabajos de construcción»
 *   45200000  group     (3)
 *   45230000  class     (4)
 *   45233000  category  (5)
 *   45233100  …and three finer levels (6, 7, 8)
 *   45233140                                    «Obras viales»
 *
 * A code is under another when it starts with the other's significant prefix.
 * The structural parent (one significant digit less) is not always a real code:
 * 11 of the 9,454 codes skip a level (e.g. `30192120`'s parent `30192100` does
 * not exist), so a consumer that needs an EXISTING parent walks
 * {@link cpvAncestors} until it finds one.
 *
 * ⚠️ Never feed a dotted UZ/KZ code to these helpers and act on the result:
 * every function here returns null (or false) for anything that is not CPV.
 */

/** Exactly the stored form: eight ASCII digits, nothing else. */
const CPV8 = /^\d{8}$/;
/** The two spellings in the wild: bare, or with the EU check digit. */
const CPV_ANY = /^(\d{8})(?:-\d)?$/;

/** Is this the stored CPV form — exactly eight digits? */
export function isCpvCode(code: string | null | undefined): code is string {
  return typeof code === 'string' && CPV8.test(code);
}

/**
 * The stored form of a CPV code: `'45233140-2'` → `'45233140'`, `' 45233140 '`
 * → `'45233140'`. Null for anything that is not a CPV code — a blank, a dotted
 * UZ/KZ code, seven or nine digits.
 */
export function canonicalCpv(code: string | null | undefined): string | null {
  if (!code) return null;
  const m = CPV_ANY.exec(code.trim());
  return m ? m[1] : null;
}

/** The significant prefix: trailing zeros dropped, at least two digits. */
export function cpvSignificant(code: string): string | null {
  if (!isCpvCode(code)) return null;
  const trimmed = code.replace(/0+$/, '');
  return trimmed.length >= 2 ? trimmed : code.slice(0, 2);
}

/**
 * Depth in the tree: 1 = division (`45000000`), 2 = group, 3 = class,
 * 4 = category, 5–7 = the finer levels. Null for a non-CPV code.
 */
export function cpvLevel(code: string): number | null {
  const sig = cpvSignificant(code);
  return sig === null ? null : sig.length - 1;
}

/** The two-digit division (`'45'`), or null for a non-CPV code. */
export function cpvDivision(code: string): string | null {
  return isCpvCode(code) ? code.slice(0, 2) : null;
}

/**
 * Every structural ancestor, nearest first, ending at the division:
 * `45233140` → `['45233100', '45233000', '45230000', '45200000', '45000000']`.
 * Empty for a division and for a non-CPV code. Not every entry need exist in
 * the vocabulary — see the module comment.
 */
export function cpvAncestors(code: string): string[] {
  const sig = cpvSignificant(code);
  if (sig === null) return [];
  const out: string[] = [];
  for (let len = sig.length - 1; len >= 2; len--) {
    out.push(sig.slice(0, len).padEnd(8, '0'));
  }
  return out;
}

/** The structural parent (one significant digit up), or null for a division. */
export function cpvParent(code: string): string | null {
  return cpvAncestors(code)[0] ?? null;
}

/**
 * Is `code` the same as, or underneath, `ancestor`? Both must be CPV codes.
 * `cpvMatches('45233140', '45000000')` → true; `cpvMatches('45233140',
 * '45100000')` → false.
 */
export function cpvMatches(code: string | null | undefined, ancestor: string): boolean {
  if (!isCpvCode(code)) return false;
  const sig = cpvSignificant(ancestor);
  return sig !== null && code.startsWith(sig);
}

/**
 * Every node of the tree a lot's CPV codes sit under, the codes themselves
 * included — what `tenders.ikpu_tree` stores, so that "is this lot under filter
 * code F" for ANY of its codes is one array membership (`ikpu_tree @> {F}`, a
 * GIN lookup) instead of a prefix test per element, which SQL arrays cannot
 * index and Prisma cannot express.
 *
 * Exact for the subtree rule (`cpvMatches`): an eight-digit F holds a code C
 * when C starts with F's significant prefix, i.e. when F is C cut after 2…8
 * digits and padded back with zeros — the code itself and {@link cpvAncestors}.
 * `['45233140', '90911200']` → `['45000000', '45200000', '45230000',
 * '45233000', '45233100', '45233140', '90000000', '90900000', '90910000',
 * '90911000', '90911200']`. Sorted, unique; a non-CPV code adds nothing.
 */
export function cpvTree(codes: readonly string[]): string[] {
  const nodes = new Set<string>();
  for (const code of codes) {
    if (!isCpvCode(code)) continue;
    nodes.add(code);
    for (const ancestor of cpvAncestors(code)) nodes.add(ancestor);
  }
  return [...nodes].sort();
}

/**
 * The items with every one whose CPV code sits UNDER another item's code
 * dropped — the broader code kept, the order kept, a repeated code kept once.
 * `['45000000', '45233140', '44113600']` → `['45000000', '44113600']`: a search
 * on the division already holds the road works, so offering both spends a slot
 * on a code that adds nothing (CPV check 22.10, «Añadir a mi alerta»). A code
 * that is not CPV (UZ/KZ) is never under anything and passes through.
 */
export function withoutNestedCpv<T>(items: readonly T[], codeOf: (item: T) => string): T[] {
  return items.filter((item, i) => {
    const code = codeOf(item);
    if (items.findIndex((other) => codeOf(other) === code) !== i) return false;
    return !items.some((other) => codeOf(other) !== code && cpvMatches(code, codeOf(other)));
  });
}

export type CpvProcurementKind = 'goods' | 'works' | 'services';

/**
 * Goods / works / services from the CPV division — the EU's own split of the
 * vocabulary (CPV guide, «Structure»): divisions 03–44 and 48 are supplies,
 * 45 is works, 50–98 are services. The ranges cover every one of the 45
 * divisions that exist; a code outside them (00–02, 46, 47, 49, 99 — no such
 * division) is not guessed and returns null.
 *
 * This is the kind of the code, which is not always the kind of the contract:
 * Spanish law (LCSP arts. 12–18) types a mixed contract by its main
 * subject, so a «suministro con instalación» or a works contract whose main
 * CPV is a product can come out differently from PLACSP's own declared type.
 *
 * ⚠️ Mirrored in SQL by migration `20261019180000_procurement_kind_cpv` (the
 * `procurement_kind` generated column). Change both or neither;
 * `procurement-kind-column.int-spec.ts` compares them.
 */
export function cpvProcurementKind(code: string | null | undefined): CpvProcurementKind | null {
  if (!isCpvCode(code)) return null;
  const division = Number(code.slice(0, 2));
  if (division === 45) return 'works';
  if ((division >= 3 && division <= 44) || division === 48) return 'goods';
  if (division >= 50 && division <= 98) return 'services';
  return null;
}
